import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, LogIn, FileText } from 'lucide-react';
import { toast } from 'sonner';

/**
 * LoginPage — redesign Luglio 2026
 *   • Gradiente animato (emerald → teal → cyan) + 3 blob a diffusione per profondità
 *   • Card glassmorphism (backdrop-blur, bordo lucido, ombra morbida)
 *   • Entrance animation stagger (header → inputs → bottone)
 *   • Hover micro-animazione sul bottone, focus-ring colorato sugli input
 *   • Tutti i data-testid originali preservati così gli e2e esistenti continuano a girare
 */
const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Compila nome utente e password');
      return;
    }
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success('Accesso effettuato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenziali non corrette');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page relative min-h-screen flex flex-col overflow-hidden">
      {/* === Sfondo gradiente animato + blob === */}
      <div className="login-bg absolute inset-0 -z-10" aria-hidden="true" />
      <div className="login-blob login-blob--1" aria-hidden="true" />
      <div className="login-blob login-blob--2" aria-hidden="true" />
      <div className="login-blob login-blob--3" aria-hidden="true" />

      {/* === Center: glass card === */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 relative z-10">
        <div className="w-full max-w-md">
          <div className="login-card rounded-3xl p-7 sm:p-9">
            {/* Logo + header */}
            <div className="login-anim-up text-center mb-6" style={{ animationDelay: '0ms' }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 border border-white/30 backdrop-blur-xl shadow-xl mb-3">
                <FileText className="w-8 h-8 text-white drop-shadow" />
              </div>
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight text-white drop-shadow-sm">
                DDT Manager
              </h1>
              <p className="text-sm text-white/70 mt-1">
                Accedi al tuo gestionale
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="login-anim-up space-y-1.5" style={{ animationDelay: '80ms' }}>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-white/80">
                  Nome utente
                </Label>
                <Input
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="es. azienda_srl"
                  className="login-input h-11 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus-visible:ring-emerald-300 focus-visible:border-emerald-200"
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <div className="login-anim-up space-y-1.5" style={{ animationDelay: '160ms' }}>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-white/80">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    data-testid="login-password-input"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="login-input pr-11 h-11 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus-visible:ring-emerald-300 focus-visible:border-emerald-200"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                    tabIndex={-1}
                    aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="login-anim-up pt-1" style={{ animationDelay: '240ms' }}>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="login-cta w-full h-12 font-semibold text-sm tracking-wider rounded-xl shadow-xl shadow-emerald-900/30 bg-white text-emerald-900 hover:bg-emerald-50 disabled:opacity-70"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="login-spinner" aria-hidden="true" />
                      ACCESSO IN CORSO…
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <LogIn className="w-4 h-4" />
                      ACCEDI
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>

          <p className="login-anim-up text-center text-xs text-white/70 mt-5" style={{ animationDelay: '320ms' }}>
            Realizzato da <span className="font-semibold text-white">Fahd Hariri</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
